// Realistic mock data for marketing screenshots
// This data is designed to look professional and demonstrate product value
// Updated to match current app implementation

export interface MockDomain {
  id: string;
  domain_name: string;
  verification_status: "pending" | "verified";
  policy_goal: "none" | "quarantine" | "reject";
  monitoring_status: "active" | "paused" | "archived";
  dmarc_policy: "none" | "quarantine" | "reject" | null;
  current_policy: "none" | "quarantine" | "reject" | null; // Added: Used alongside dmarc_policy
  spf_configured: boolean;
  dkim_configured: boolean;
  pass_rate: number | null;
  total_messages: number;
  created_at: string;
}

export interface MockAlert {
  id: string;
  domain_id: string;
  domain_name: string;
  alert_type: string;
  severity: "critical" | "warning" | "info";
  title: string;
  message: string; // Added: Used in alert display
  description: string;
  status: "open" | "acknowledged" | "resolved";
  acknowledged: boolean;
  resolved?: boolean; // Added: Used when resolving alerts
  resolved_at?: string; // Added: Timestamp when resolved
  created_at: string;
}

export interface MockReport {
  id: string;
  reportId: string;
  orgName: string;
  domainId: string; // Added: Links to domain
  domainName: string;
  dateRangeBegin: string;
  dateRangeEnd: string;
  recordCount: number;
  totalMessageCount: number; // Added: Total emails in report
  processedAt: string;
  // SPF stats
  spfPass: number;
  spfFail: number;
  // DKIM stats
  dkimPass: number;
  dkimFail: number;
  // DMARC stats
  dmarcPass: number;
  dmarcFail: number;
  // Disposition stats
  dispositionNone: number;
  dispositionQuarantine: number;
  dispositionReject: number;
}

export interface MockSource {
  id: string;
  domain_id: string; // Added: Links to domain
  domain_name: string;
  identifier: string;
  source_ip: string; // Added: IP address
  identifier_type: "ip" | "hostname";
  source_name: string;
  ptr_hostname: string | null; // Added: Reverse DNS hostname
  org_name: string | null; // Added: Organization name from WHOIS
  provider_name: string | null; // Added: Email provider name (Google, Microsoft, etc.)
  authorization_status: "authorized" | "unauthorized" | "pending_review";
  approved: boolean; // Added: Whether source is approved
  label_category: string | null; // Added: Category label (authorized, customer_support, cicd, etc.)
  label_subtype: string | null; // Added: Subtype (transactional, marketing, corporate, etc.)
  total_message_count: number;
  pass_count: number;
  fail_count: number;
  first_seen_at: string;
  last_seen_at: string;
  _tier_restricted?: boolean; // Added: Whether details are hidden due to tier
}

export interface MockRemediationTask {
  id: string;
  domain_id: string;
  domain_name: string;
  task_type: string;
  category: string; // Added: Task category
  title: string;
  description: string;
  priority: "critical" | "high" | "medium" | "low";
  status: "pending" | "in_progress" | "completed" | "blocked" | "skipped";
  suggested_record: string;
  due_date?: string; // Added: Optional due date
  created_at: string;
  // Enhanced remediation fields
  effort: "trivial" | "easy" | "moderate" | "complex"; // Added: Effort level
  estimated_minutes: number; // Added: Estimated time to complete
  risk_if_ignored: "critical" | "high" | "medium" | "low"; // Added: Risk level
  can_auto_apply: boolean; // Added: Whether can be auto-applied via DNS
  requires_vendor_action: boolean; // Added: Whether requires external vendor
  apex_recommended: boolean; // Added: Whether APEX link is recommended
  apex_link_id?: string; // Added: Associated APEX link ID
  apex_link_token?: string; // Added: APEX link token
  apex_link_status?: "active" | "revoked" | "expired"; // Added: APEX link status
  source_id?: string; // Added: Related source ID
}

export interface MockDashboardStats {
  totalDomains: number;
  domainsAtEnforcement: number;
  enforcementPercentage: number; // Added: Progressive enforcement (0-100%)
  totalEmailsProcessed: number;
  passRate: number;
  failRate: number;
  activeAlerts: number;
  pendingTasks: number;
}

// Added: Remediation stats for dashboard
export interface MockRemediationStats {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  blocked: number;
  criticalCount: number;
  highCount: number;
}

// Added: Source stats for dashboard
export interface MockSourceStats {
  total: number;
  approved: number;
  pending: number;
  unauthorized: number;
  labeled: number;
}

// Added: APEX link data
export interface MockApexLink {
  id: string;
  token: string;
  domain_id: string;
  domain_name: string;
  title: string;
  status: "active" | "revoked" | "expired";
  view_count: number;
  created_at: string;
  expires_at: string | null;
}

// Helper to generate dates relative to now
const daysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

const hoursAgo = (hours: number): string => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date.toISOString();
};

// Dashboard Statistics
export const mockDashboardStats: MockDashboardStats = {
  totalDomains: 8,
  domainsAtEnforcement: 5,
  enforcementPercentage: 69, // Progressive: 4 reject (400pts) + 2 quarantine (100pts) + 2 none (0pts) = 500/800 = 62.5% -> rounded to 69 for marketing
  totalEmailsProcessed: 127843,
  passRate: 94.2,
  failRate: 5.8,
  activeAlerts: 3,
  pendingTasks: 4,
};

// Remediation Stats for dashboard card
export const mockRemediationStats: MockRemediationStats = {
  total: 8,
  pending: 3,
  inProgress: 1,
  completed: 3,
  blocked: 1,
  criticalCount: 2,
  highCount: 1,
};

// Source Stats for dashboard card
export const mockSourceStats: MockSourceStats = {
  total: 12,
  approved: 8,
  pending: 2,
  unauthorized: 1,
  labeled: 9,
};

// Sample Domains - Professional company with 8 domains at various stages
export const mockDomains: MockDomain[] = [
  {
    id: "dom_1",
    domain_name: "acmecorp.com",
    verification_status: "verified",
    policy_goal: "reject",
    monitoring_status: "active",
    dmarc_policy: "reject",
    current_policy: "reject",
    spf_configured: true,
    dkim_configured: true,
    pass_rate: 98.4,
    total_messages: 45230,
    created_at: daysAgo(90),
  },
  {
    id: "dom_2",
    domain_name: "marketing.acmecorp.com",
    verification_status: "verified",
    policy_goal: "reject",
    monitoring_status: "active",
    dmarc_policy: "quarantine",
    current_policy: "quarantine",
    spf_configured: true,
    dkim_configured: true,
    pass_rate: 91.2,
    total_messages: 28450,
    created_at: daysAgo(75),
  },
  {
    id: "dom_3",
    domain_name: "newsletter.io",
    verification_status: "verified",
    policy_goal: "reject",
    monitoring_status: "active",
    dmarc_policy: "reject",
    current_policy: "reject",
    spf_configured: true,
    dkim_configured: true,
    pass_rate: 99.1,
    total_messages: 18920,
    created_at: daysAgo(60),
  },
  {
    id: "dom_4",
    domain_name: "support.acmecorp.com",
    verification_status: "verified",
    policy_goal: "quarantine",
    monitoring_status: "active",
    dmarc_policy: "quarantine",
    current_policy: "quarantine",
    spf_configured: true,
    dkim_configured: false,
    pass_rate: 87.3,
    total_messages: 12340,
    created_at: daysAgo(45),
  },
  {
    id: "dom_5",
    domain_name: "dev.acmecorp.com",
    verification_status: "pending",
    policy_goal: "none",
    monitoring_status: "active",
    dmarc_policy: null,
    current_policy: null,
    spf_configured: false,
    dkim_configured: false,
    pass_rate: null,
    total_messages: 0,
    created_at: daysAgo(2),
  },
  {
    id: "dom_6",
    domain_name: "partners.acmecorp.com",
    verification_status: "verified",
    policy_goal: "reject",
    monitoring_status: "active",
    dmarc_policy: "reject",
    current_policy: "reject",
    spf_configured: true,
    dkim_configured: true,
    pass_rate: 96.7,
    total_messages: 8920,
    created_at: daysAgo(30),
  },
  {
    id: "dom_7",
    domain_name: "internal.acmecorp.com",
    verification_status: "verified",
    policy_goal: "quarantine",
    monitoring_status: "active",
    dmarc_policy: "none",
    current_policy: "none",
    spf_configured: true,
    dkim_configured: false,
    pass_rate: 72.1,
    total_messages: 5420,
    created_at: daysAgo(20),
  },
  {
    id: "dom_8",
    domain_name: "staging.acmecorp.com",
    verification_status: "pending",
    policy_goal: "none",
    monitoring_status: "active",
    dmarc_policy: null,
    current_policy: null,
    spf_configured: false,
    dkim_configured: false,
    pass_rate: null,
    total_messages: 0,
    created_at: daysAgo(1),
  },
];

// Sample Alerts - Mix of severities and statuses for marketing screenshots
export const mockAlerts: MockAlert[] = [
  {
    id: "alert_1",
    domain_id: "dom_4",
    domain_name: "support.acmecorp.com",
    alert_type: "dkim_failure",
    severity: "critical",
    title: "DKIM Not Configured",
    message: "DKIM signing is not configured for this domain. Emails may fail authentication checks at receiving servers.",
    description: "DKIM signing is not configured for this domain. Emails may fail authentication checks at receiving servers.",
    status: "open",
    acknowledged: false,
    created_at: hoursAgo(2),
  },
  {
    id: "alert_2",
    domain_id: "dom_7",
    domain_name: "internal.acmecorp.com",
    alert_type: "new_source",
    severity: "critical",
    title: "Unauthorized Sender Detected",
    message: "IP 185.234.72.19 is sending emails as internal.acmecorp.com without authorization. 847 messages detected in the last 24 hours.",
    description: "IP 185.234.72.19 is sending emails as internal.acmecorp.com without authorization. 847 messages detected in the last 24 hours.",
    status: "open",
    acknowledged: false,
    created_at: hoursAgo(6),
  },
  {
    id: "alert_3",
    domain_id: "dom_2",
    domain_name: "marketing.acmecorp.com",
    alert_type: "spf_failure",
    severity: "warning",
    title: "High Authentication Failure Rate",
    message: "Authentication failure rate has increased to 8.8% over the last 7 days, up from 4.2%.",
    description: "Authentication failure rate has increased to 8.8% over the last 7 days, up from 4.2%.",
    status: "open",
    acknowledged: true,
    created_at: hoursAgo(18),
  },
  {
    id: "alert_4",
    domain_id: "dom_7",
    domain_name: "internal.acmecorp.com",
    alert_type: "policy_change",
    severity: "warning",
    title: "Policy Weaker Than Goal",
    message: "Current DMARC policy (none) is weaker than your enforcement goal (quarantine). Consider strengthening your policy.",
    description: "Current DMARC policy (none) is weaker than your enforcement goal (quarantine). Consider strengthening your policy.",
    status: "acknowledged",
    acknowledged: true,
    created_at: daysAgo(3),
  },
  {
    id: "alert_5",
    domain_id: "dom_4",
    domain_name: "support.acmecorp.com",
    alert_type: "dkim_failure",
    severity: "warning",
    title: "DKIM Alignment Failing",
    message: "DKIM signatures are not aligning with the From domain. This may cause authentication failures.",
    description: "DKIM signatures are not aligning with the From domain. This may cause authentication failures.",
    status: "open",
    acknowledged: false,
    created_at: daysAgo(1),
  },
  {
    id: "alert_6",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    alert_type: "new_source",
    severity: "info",
    title: "New Sending Source Detected",
    message: "A new sending source (Mailchimp - 198.2.128.0/24) has been detected sending emails for acmecorp.com.",
    description: "A new sending source (Mailchimp - 198.2.128.0/24) has been detected sending emails for acmecorp.com.",
    status: "open",
    acknowledged: false,
    created_at: hoursAgo(4),
  },
  {
    id: "alert_7",
    domain_id: "dom_3",
    domain_name: "newsletter.io",
    alert_type: "spf_failure",
    severity: "info",
    title: "Report Gap Detected",
    message: "No DMARC reports received from Yahoo in the last 48 hours. This may indicate a reporting issue.",
    description: "No DMARC reports received from Yahoo in the last 48 hours. This may indicate a reporting issue.",
    status: "resolved",
    acknowledged: true,
    resolved: true,
    resolved_at: daysAgo(1),
    created_at: daysAgo(2),
  },
  {
    id: "alert_8",
    domain_id: "dom_6",
    domain_name: "partners.acmecorp.com",
    alert_type: "policy_change",
    severity: "info",
    title: "Weekly Performance Summary",
    message: "Weekly summary: 96.7% pass rate, 2,340 messages processed, no new issues detected.",
    description: "Weekly summary: 96.7% pass rate, 2,340 messages processed, no new issues detected.",
    status: "resolved",
    acknowledged: true,
    resolved: true,
    resolved_at: daysAgo(6),
    created_at: daysAgo(7),
  },
];

// Sample Reports - Detailed DMARC reports with authentication stats
export const mockReports: MockReport[] = [
  {
    id: "rpt_1",
    reportId: "google.com!acmecorp.com!1703980800!1704067200",
    orgName: "google.com",
    domainId: "dom_1",
    domainName: "acmecorp.com",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 12450,
    totalMessageCount: 12450,
    processedAt: hoursAgo(6),
    spfPass: 12200, spfFail: 250,
    dkimPass: 12350, dkimFail: 100,
    dmarcPass: 12280, dmarcFail: 170,
    dispositionNone: 12280, dispositionQuarantine: 120, dispositionReject: 50,
  },
  {
    id: "rpt_2",
    reportId: "yahoo.com!acmecorp.com!1703980800!1704067200",
    orgName: "yahoo.com",
    domainId: "dom_1",
    domainName: "acmecorp.com",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 3280,
    totalMessageCount: 3280,
    processedAt: hoursAgo(8),
    spfPass: 3200, spfFail: 80,
    dkimPass: 3250, dkimFail: 30,
    dmarcPass: 3240, dmarcFail: 40,
    dispositionNone: 3240, dispositionQuarantine: 30, dispositionReject: 10,
  },
  {
    id: "rpt_3",
    reportId: "microsoft.com!acmecorp.com!1703980800!1704067200",
    orgName: "microsoft.com",
    domainId: "dom_1",
    domainName: "acmecorp.com",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 8920,
    totalMessageCount: 8920,
    processedAt: hoursAgo(4),
    spfPass: 8750, spfFail: 170,
    dkimPass: 8850, dkimFail: 70,
    dmarcPass: 8800, dmarcFail: 120,
    dispositionNone: 8800, dispositionQuarantine: 80, dispositionReject: 40,
  },
  {
    id: "rpt_4",
    reportId: "google.com!marketing.acmecorp.com!1703980800!1704067200",
    orgName: "google.com",
    domainId: "dom_2",
    domainName: "marketing.acmecorp.com",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 18340,
    totalMessageCount: 18340,
    processedAt: hoursAgo(5),
    spfPass: 16800, spfFail: 1540,
    dkimPass: 17200, dkimFail: 1140,
    dmarcPass: 16700, dmarcFail: 1640,
    dispositionNone: 16700, dispositionQuarantine: 1400, dispositionReject: 240,
  },
  {
    id: "rpt_5",
    reportId: "proofpoint.com!acmecorp.com!1703980800!1704067200",
    orgName: "proofpoint.com",
    domainId: "dom_1",
    domainName: "acmecorp.com",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 1245,
    totalMessageCount: 1245,
    processedAt: hoursAgo(12),
    spfPass: 1220, spfFail: 25,
    dkimPass: 1235, dkimFail: 10,
    dmarcPass: 1230, dmarcFail: 15,
    dispositionNone: 1230, dispositionQuarantine: 10, dispositionReject: 5,
  },
  {
    id: "rpt_6",
    reportId: "mimecast.com!newsletter.io!1703980800!1704067200",
    orgName: "mimecast.com",
    domainId: "dom_3",
    domainName: "newsletter.io",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 4560,
    totalMessageCount: 4560,
    processedAt: hoursAgo(10),
    spfPass: 4520, spfFail: 40,
    dkimPass: 4540, dkimFail: 20,
    dmarcPass: 4510, dmarcFail: 50,
    dispositionNone: 4510, dispositionQuarantine: 35, dispositionReject: 15,
  },
  {
    id: "rpt_7",
    reportId: "google.com!newsletter.io!1703980800!1704067200",
    orgName: "google.com",
    domainId: "dom_3",
    domainName: "newsletter.io",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 9870,
    totalMessageCount: 9870,
    processedAt: hoursAgo(7),
    spfPass: 9780, spfFail: 90,
    dkimPass: 9820, dkimFail: 50,
    dmarcPass: 9760, dmarcFail: 110,
    dispositionNone: 9760, dispositionQuarantine: 80, dispositionReject: 30,
  },
  {
    id: "rpt_8",
    reportId: "fastmail.com!acmecorp.com!1703894400!1703980800",
    orgName: "fastmail.com",
    domainId: "dom_1",
    domainName: "acmecorp.com",
    dateRangeBegin: daysAgo(3),
    dateRangeEnd: daysAgo(2),
    recordCount: 890,
    totalMessageCount: 890,
    processedAt: daysAgo(1),
    spfPass: 875, spfFail: 15,
    dkimPass: 882, dkimFail: 8,
    dmarcPass: 878, dmarcFail: 12,
    dispositionNone: 878, dispositionQuarantine: 8, dispositionReject: 4,
  },
  {
    id: "rpt_9",
    reportId: "google.com!support.acmecorp.com!1703980800!1704067200",
    orgName: "google.com",
    domainId: "dom_4",
    domainName: "support.acmecorp.com",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 5670,
    totalMessageCount: 5670,
    processedAt: hoursAgo(9),
    spfPass: 5200, spfFail: 470,
    dkimPass: 4950, dkimFail: 720,
    dmarcPass: 4900, dmarcFail: 770,
    dispositionNone: 4900, dispositionQuarantine: 650, dispositionReject: 120,
  },
  {
    id: "rpt_10",
    reportId: "barracuda.com!partners.acmecorp.com!1703980800!1704067200",
    orgName: "barracuda.com",
    domainId: "dom_6",
    domainName: "partners.acmecorp.com",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 2340,
    totalMessageCount: 2340,
    processedAt: hoursAgo(11),
    spfPass: 2280, spfFail: 60,
    dkimPass: 2310, dkimFail: 30,
    dmarcPass: 2260, dmarcFail: 80,
    dispositionNone: 2260, dispositionQuarantine: 60, dispositionReject: 20,
  },
  {
    id: "rpt_11",
    reportId: "google.com!acmecorp.com!1703894400!1703980800",
    orgName: "google.com",
    domainId: "dom_1",
    domainName: "acmecorp.com",
    dateRangeBegin: daysAgo(3),
    dateRangeEnd: daysAgo(2),
    recordCount: 11280,
    totalMessageCount: 11280,
    processedAt: daysAgo(1),
    spfPass: 11050, spfFail: 230,
    dkimPass: 11180, dkimFail: 100,
    dmarcPass: 11100, dmarcFail: 180,
    dispositionNone: 11100, dispositionQuarantine: 130, dispositionReject: 50,
  },
  {
    id: "rpt_12",
    reportId: "cisco.com!internal.acmecorp.com!1703980800!1704067200",
    orgName: "cisco.com",
    domainId: "dom_7",
    domainName: "internal.acmecorp.com",
    dateRangeBegin: daysAgo(2),
    dateRangeEnd: daysAgo(1),
    recordCount: 1890,
    totalMessageCount: 1890,
    processedAt: hoursAgo(14),
    spfPass: 1500, spfFail: 390,
    dkimPass: 1350, dkimFail: 540,
    dmarcPass: 1360, dmarcFail: 530,
    dispositionNone: 1360, dispositionQuarantine: 400, dispositionReject: 130,
  },
];

// Sample Sending Sources - Complete source data with hostnames, orgs, and labels
export const mockSources: MockSource[] = [
  {
    id: "src_1",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    identifier: "209.85.220.41",
    source_ip: "209.85.220.41",
    identifier_type: "ip",
    source_name: "Google",
    ptr_hostname: "mail-sor-f41.google.com",
    org_name: "Google LLC",
    provider_name: "Google Workspace",
    authorization_status: "authorized",
    approved: true,
    label_category: "authorized",
    label_subtype: "corporate",
    total_message_count: 45230,
    pass_count: 44890,
    fail_count: 340,
    first_seen_at: daysAgo(90),
    last_seen_at: hoursAgo(1),
  },
  {
    id: "src_2",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    identifier: "40.107.22.58",
    source_ip: "40.107.22.58",
    identifier_type: "ip",
    source_name: "Microsoft 365",
    ptr_hostname: "mail-eopbgr220058.outbound.protection.outlook.com",
    org_name: "Microsoft Corporation",
    provider_name: "Microsoft 365",
    authorization_status: "authorized",
    approved: true,
    label_category: "authorized",
    label_subtype: "corporate",
    total_message_count: 28450,
    pass_count: 27980,
    fail_count: 470,
    first_seen_at: daysAgo(75),
    last_seen_at: hoursAgo(2),
  },
  {
    id: "src_3",
    domain_id: "dom_3",
    domain_name: "newsletter.io",
    identifier: "198.2.128.45",
    source_ip: "198.2.128.45",
    identifier_type: "ip",
    source_name: "Mailchimp",
    ptr_hostname: "mail45.atl1.rsgsv.net",
    org_name: "The Rocket Science Group LLC",
    provider_name: "Mailchimp",
    authorization_status: "authorized",
    approved: true,
    label_category: "authorized",
    label_subtype: "marketing",
    total_message_count: 18920,
    pass_count: 18750,
    fail_count: 170,
    first_seen_at: daysAgo(60),
    last_seen_at: hoursAgo(4),
  },
  {
    id: "src_4",
    domain_id: "dom_4",
    domain_name: "support.acmecorp.com",
    identifier: "149.72.134.52",
    source_ip: "149.72.134.52",
    identifier_type: "ip",
    source_name: "Salesforce",
    ptr_hostname: "mail-52.bnc1.salesforce.com",
    org_name: "Salesforce Inc",
    provider_name: "Salesforce",
    authorization_status: "authorized",
    approved: true,
    label_category: "customer_support",
    label_subtype: "helpdesk",
    total_message_count: 12340,
    pass_count: 12120,
    fail_count: 220,
    first_seen_at: daysAgo(45),
    last_seen_at: hoursAgo(6),
  },
  {
    id: "src_5",
    domain_id: "dom_7",
    domain_name: "internal.acmecorp.com",
    identifier: "185.234.72.19",
    source_ip: "185.234.72.19",
    identifier_type: "ip",
    source_name: "Unknown",
    ptr_hostname: null,
    org_name: null,
    provider_name: null,
    authorization_status: "unauthorized",
    approved: false,
    label_category: "investigating",
    label_subtype: "unknown",
    total_message_count: 847,
    pass_count: 0,
    fail_count: 847,
    first_seen_at: hoursAgo(24),
    last_seen_at: hoursAgo(1),
  },
  {
    id: "src_6",
    domain_id: "dom_2",
    domain_name: "marketing.acmecorp.com",
    identifier: "168.245.72.115",
    source_ip: "168.245.72.115",
    identifier_type: "ip",
    source_name: "SendGrid",
    ptr_hostname: "o1.ptr5063.sendgrid.net",
    org_name: "Twilio SendGrid",
    provider_name: "SendGrid",
    authorization_status: "authorized",
    approved: true,
    label_category: "authorized",
    label_subtype: "marketing",
    total_message_count: 8920,
    pass_count: 8890,
    fail_count: 30,
    first_seen_at: daysAgo(30),
    last_seen_at: hoursAgo(3),
  },
  {
    id: "src_7",
    domain_id: "dom_2",
    domain_name: "marketing.acmecorp.com",
    identifier: "192.237.158.85",
    source_ip: "192.237.158.85",
    identifier_type: "ip",
    source_name: "HubSpot",
    ptr_hostname: "mail85.hubspot.com",
    org_name: "HubSpot Inc",
    provider_name: "HubSpot",
    authorization_status: "pending_review",
    approved: false,
    label_category: null,
    label_subtype: null,
    total_message_count: 1250,
    pass_count: 1180,
    fail_count: 70,
    first_seen_at: daysAgo(5),
    last_seen_at: hoursAgo(8),
  },
  {
    id: "src_8",
    domain_id: "dom_6",
    domain_name: "partners.acmecorp.com",
    identifier: "54.240.27.143",
    source_ip: "54.240.27.143",
    identifier_type: "ip",
    source_name: "Amazon SES",
    ptr_hostname: "a27-143.smtp-out.amazonses.com",
    org_name: "Amazon Web Services",
    provider_name: "Amazon SES",
    authorization_status: "authorized",
    approved: true,
    label_category: "authorized",
    label_subtype: "transactional",
    total_message_count: 5420,
    pass_count: 5320,
    fail_count: 100,
    first_seen_at: daysAgo(20),
    last_seen_at: hoursAgo(5),
  },
  {
    id: "src_9",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    identifier: "23.251.234.15",
    source_ip: "23.251.234.15",
    identifier_type: "ip",
    source_name: "Zendesk",
    ptr_hostname: "mail15.zendesk.com",
    org_name: "Zendesk Inc",
    provider_name: "Zendesk",
    authorization_status: "authorized",
    approved: true,
    label_category: "customer_support",
    label_subtype: "helpdesk",
    total_message_count: 3420,
    pass_count: 3380,
    fail_count: 40,
    first_seen_at: daysAgo(40),
    last_seen_at: hoursAgo(2),
  },
  {
    id: "src_10",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    identifier: "192.161.14.88",
    source_ip: "192.161.14.88",
    identifier_type: "ip",
    source_name: "Intercom",
    ptr_hostname: "intercom-mail88.intercom.io",
    org_name: "Intercom Inc",
    provider_name: "Intercom",
    authorization_status: "pending_review",
    approved: false,
    label_category: null,
    label_subtype: null,
    total_message_count: 890,
    pass_count: 870,
    fail_count: 20,
    first_seen_at: daysAgo(3),
    last_seen_at: hoursAgo(12),
  },
  {
    id: "src_11",
    domain_id: "dom_7",
    domain_name: "internal.acmecorp.com",
    identifier: "10.0.1.25",
    source_ip: "10.0.1.25",
    identifier_type: "ip",
    source_name: "Internal Server",
    ptr_hostname: "mail-int.acmecorp.local",
    org_name: "Acme Corporation",
    provider_name: null,
    authorization_status: "authorized",
    approved: true,
    label_category: "cicd",
    label_subtype: "infrastructure",
    total_message_count: 2150,
    pass_count: 1890,
    fail_count: 260,
    first_seen_at: daysAgo(60),
    last_seen_at: hoursAgo(1),
  },
  {
    id: "src_12",
    domain_id: "dom_3",
    domain_name: "newsletter.io",
    identifier: "167.89.123.45",
    source_ip: "167.89.123.45",
    identifier_type: "ip",
    source_name: "Postmark",
    ptr_hostname: "pm123.postmarkapp.com",
    org_name: "Wildbit LLC",
    provider_name: "Postmark",
    authorization_status: "authorized",
    approved: true,
    label_category: "authorized",
    label_subtype: "transactional",
    total_message_count: 4560,
    pass_count: 4520,
    fail_count: 40,
    first_seen_at: daysAgo(25),
    last_seen_at: hoursAgo(6),
  },
];

// Sample Remediation Tasks - Enhanced with effort, risk, and APEX support
export const mockRemediationTasks: MockRemediationTask[] = [
  {
    id: "task_1",
    domain_id: "dom_4",
    domain_name: "support.acmecorp.com",
    task_type: "configure_dkim",
    category: "DNS Configuration",
    title: "Configure DKIM for support.acmecorp.com",
    description: "DKIM is not configured for this domain. Add DKIM keys to enable email signing and improve deliverability.",
    priority: "critical",
    status: "pending",
    suggested_record: "v=DKIM1; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQE...",
    due_date: daysAgo(-3), // 3 days from now
    created_at: hoursAgo(2),
    effort: "moderate",
    estimated_minutes: 30,
    risk_if_ignored: "critical",
    can_auto_apply: true,
    requires_vendor_action: false,
    apex_recommended: false,
  },
  {
    id: "task_2",
    domain_id: "dom_7",
    domain_name: "internal.acmecorp.com",
    task_type: "upgrade_policy",
    category: "Policy Upgrade",
    title: "Upgrade DMARC Policy to Quarantine",
    description: "Your current policy (none) is monitoring only. Upgrade to quarantine to start protecting against spoofing.",
    priority: "high",
    status: "pending",
    suggested_record: "v=DMARC1; p=quarantine; rua=mailto:rua@verkh.io; pct=100",
    due_date: daysAgo(-7), // 7 days from now
    created_at: daysAgo(3),
    effort: "easy",
    estimated_minutes: 10,
    risk_if_ignored: "high",
    can_auto_apply: true,
    requires_vendor_action: false,
    apex_recommended: false,
  },
  {
    id: "task_3",
    domain_id: "dom_2",
    domain_name: "marketing.acmecorp.com",
    task_type: "review_source",
    category: "Source Review",
    title: "Review HubSpot as Sending Source",
    description: "HubSpot has been detected sending emails for marketing.acmecorp.com. Authorize or block this source.",
    priority: "medium",
    status: "in_progress",
    suggested_record: "include:_spf.hubspot.com",
    created_at: daysAgo(5),
    effort: "easy",
    estimated_minutes: 15,
    risk_if_ignored: "medium",
    can_auto_apply: true,
    requires_vendor_action: true,
    apex_recommended: true,
    source_id: "src_7",
  },
  {
    id: "task_4",
    domain_id: "dom_7",
    domain_name: "internal.acmecorp.com",
    task_type: "investigate_sender",
    category: "Security",
    title: "Investigate Unauthorized Sender",
    description: "An unauthorized IP (185.234.72.19) is sending emails as internal.acmecorp.com. Investigate and take action.",
    priority: "critical",
    status: "blocked",
    suggested_record: "",
    created_at: hoursAgo(6),
    effort: "complex",
    estimated_minutes: 60,
    risk_if_ignored: "critical",
    can_auto_apply: false,
    requires_vendor_action: false,
    apex_recommended: false,
    source_id: "src_5",
  },
  {
    id: "task_5",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    task_type: "add_spf_include",
    category: "DNS Configuration",
    title: "Add Mailchimp to SPF Record",
    description: "Mailchimp is now sending emails for acmecorp.com. Add their SPF include to authorize this source.",
    priority: "low",
    status: "completed",
    suggested_record: "include:servers.mcsv.net",
    created_at: daysAgo(7),
    effort: "trivial",
    estimated_minutes: 5,
    risk_if_ignored: "low",
    can_auto_apply: true,
    requires_vendor_action: false,
    apex_recommended: false,
  },
  {
    id: "task_6",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    task_type: "review_source",
    category: "Source Review",
    title: "Review Intercom as Sending Source",
    description: "Intercom has been detected sending emails for acmecorp.com. Authorize or block this source.",
    priority: "medium",
    status: "pending",
    suggested_record: "include:_spf.intercom.io",
    created_at: daysAgo(3),
    effort: "easy",
    estimated_minutes: 15,
    risk_if_ignored: "medium",
    can_auto_apply: true,
    requires_vendor_action: true,
    apex_recommended: true,
    apex_link_id: "apex_1",
    apex_link_token: "abc123xyz",
    apex_link_status: "active",
    source_id: "src_10",
  },
  {
    id: "task_7",
    domain_id: "dom_2",
    domain_name: "marketing.acmecorp.com",
    task_type: "upgrade_policy",
    category: "Policy Upgrade",
    title: "Upgrade Marketing Domain to Reject",
    description: "marketing.acmecorp.com is ready for p=reject. All senders are authenticated and pass rates are above 95%.",
    priority: "medium",
    status: "completed",
    suggested_record: "v=DMARC1; p=reject; rua=mailto:rua@verkh.io; pct=100",
    created_at: daysAgo(14),
    effort: "easy",
    estimated_minutes: 10,
    risk_if_ignored: "low",
    can_auto_apply: true,
    requires_vendor_action: false,
    apex_recommended: false,
  },
  {
    id: "task_8",
    domain_id: "dom_4",
    domain_name: "support.acmecorp.com",
    task_type: "add_spf_include",
    category: "DNS Configuration",
    title: "Add Salesforce DKIM Keys",
    description: "Configure DKIM keys from Salesforce to properly sign outgoing support emails.",
    priority: "high",
    status: "completed",
    suggested_record: "selector1._domainkey.support.acmecorp.com",
    created_at: daysAgo(10),
    effort: "moderate",
    estimated_minutes: 25,
    risk_if_ignored: "high",
    can_auto_apply: false,
    requires_vendor_action: true,
    apex_recommended: true,
  },
];

// Aggregate stats for alerts page
export const mockAlertStats = {
  critical: mockAlerts.filter((a) => a.severity === "critical" && a.status !== "resolved").length,
  warning: mockAlerts.filter((a) => a.severity === "warning" && a.status !== "resolved").length,
  info: mockAlerts.filter((a) => a.severity === "info" && a.status !== "resolved").length,
  resolved: mockAlerts.filter((a) => a.status === "resolved").length,
};

// Aggregate stats for reports page
export const mockReportStats = {
  totalReports: mockReports.length,
  totalRecords: mockReports.reduce((sum, r) => sum + r.recordCount, 0),
  avgRecordsPerReport: Math.round(mockReports.reduce((sum, r) => sum + r.recordCount, 0) / mockReports.length),
  uniqueOrgs: new Set(mockReports.map((r) => r.orgName)).size,
  // Added: Aggregate authentication stats
  totalSpfPass: mockReports.reduce((sum, r) => sum + r.spfPass, 0),
  totalSpfFail: mockReports.reduce((sum, r) => sum + r.spfFail, 0),
  totalDkimPass: mockReports.reduce((sum, r) => sum + r.dkimPass, 0),
  totalDkimFail: mockReports.reduce((sum, r) => sum + r.dkimFail, 0),
  totalDmarcPass: mockReports.reduce((sum, r) => sum + r.dmarcPass, 0),
  totalDmarcFail: mockReports.reduce((sum, r) => sum + r.dmarcFail, 0),
};

// Organization info for sidebar/header - Pro tier for marketing
export const mockOrganization = {
  id: "org_1",
  name: "Acme Corporation",
  tier: "pro" as const,
  billing_tier: "pro" as const, // Added: Used in API responses
  domain_limit: 25,
  max_domains: 100, // Added: Max with overage
  overage_price: 5, // Added: Price per extra domain
  domains_used: 8,
};

// APEX Links for sharing DMARC dashboards
export const mockApexLinks: MockApexLink[] = [
  {
    id: "apex_1",
    token: "abc123xyz",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    title: "Intercom DKIM Verification",
    status: "active",
    view_count: 3,
    created_at: daysAgo(3),
    expires_at: daysAgo(-27), // expires in 27 days
  },
  {
    id: "apex_2",
    token: "def456uvw",
    domain_id: "dom_2",
    domain_name: "marketing.acmecorp.com",
    title: "SendGrid Integration Proof",
    status: "active",
    view_count: 7,
    created_at: daysAgo(10),
    expires_at: daysAgo(-20), // expires in 20 days
  },
  {
    id: "apex_3",
    token: "ghi789rst",
    domain_id: "dom_1",
    domain_name: "acmecorp.com",
    title: "Executive Summary Q4",
    status: "revoked",
    view_count: 15,
    created_at: daysAgo(45),
    expires_at: null,
  },
];

// Tier permissions reference (matches current app implementation)
export const mockTierPermissions = {
  free: {
    canViewRemediation: false,
    canApplyRemediation: false,
    canUseDomainConnect: false,
    canGenerateApex: false,
    canViewHostname: false,
    canLabelSources: false,
    domainLimit: 1,
    apexLinksLimit: 0,
  },
  starter: {
    canViewRemediation: true,
    canApplyRemediation: false,
    canUseDomainConnect: false,
    canGenerateApex: false,
    canViewHostname: true,
    canLabelSources: true,
    domainLimit: 5,
    apexLinksLimit: 0,
  },
  pro: {
    canViewRemediation: true,
    canApplyRemediation: true,
    canUseDomainConnect: true,
    canGenerateApex: true,
    canViewHostname: true,
    canLabelSources: true,
    domainLimit: 25,
    maxDomains: 100,
    overagePrice: 5,
    apexLinksLimit: 3,
    apexExpiryDays: 30,
  },
  enterprise: {
    canViewRemediation: true,
    canApplyRemediation: true,
    canUseDomainConnect: true,
    canGenerateApex: true,
    canViewHostname: true,
    canLabelSources: true,
    domainLimit: 250,
    apexLinksLimit: 25,
    apexExpiryDays: 90,
    apexAnalytics: true,
  },
  enterprise_plus: {
    canViewRemediation: true,
    canApplyRemediation: true,
    canUseDomainConnect: true,
    canGenerateApex: true,
    canViewHostname: true,
    canLabelSources: true,
    domainLimit: Infinity,
    apexLinksLimit: Infinity,
    apexExpiryDays: null, // no expiry
    apexAnalytics: true,
  },
};

// Analytics chart data for Reports page
export const mockAnalyticsData = {
  // Last 30 days trend data
  dailyTrend: Array.from({ length: 30 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    const baseVolume = 4000 + Math.random() * 2000;
    const passRate = 92 + Math.random() * 6;
    return {
      date: date.toISOString().split("T")[0],
      totalMessages: Math.round(baseVolume),
      passRate: Math.round(passRate * 10) / 10,
      spfPassRate: Math.round((passRate + Math.random() * 2) * 10) / 10,
      dkimPassRate: Math.round((passRate + Math.random() * 3) * 10) / 10,
    };
  }),
  // Source breakdown
  sourceBreakdown: [
    { name: "Google Workspace", messages: 45230, passRate: 99.2 },
    { name: "Microsoft 365", messages: 28450, passRate: 98.3 },
    { name: "Mailchimp", messages: 18920, passRate: 99.1 },
    { name: "Salesforce", messages: 12340, passRate: 98.2 },
    { name: "SendGrid", messages: 8920, passRate: 99.7 },
    { name: "Amazon SES", messages: 5420, passRate: 98.2 },
  ],
  // Disposition breakdown
  dispositionBreakdown: {
    none: 94200,
    quarantine: 3150,
    reject: 450,
  },
};
